<template>
  <v-container>
    <v-layout row>
      <v-flex>
        <v-card-text>
          <v-container>
            <h2>IABF {{ adminFestivalYear }} Choreographer Sheet</h2>
            <v-btn @click="exportCsv">
              Export CSV
            </v-btn>

            <table class="choreo-table mt-4">
              <thead>
                <tr>
                  <th class="col-name">Name</th>
                  <th class="col-piece">Waltz of the Hours</th>
                  <th class="col-piece">WoH: Pirouette</th>
                  <th class="col-piece">WoH: Learned Broche?</th>
                  <th class="col-piece">Alice in Wonderland</th>
                  <th class="col-piece">Le Jazz Hot</th>
                  <th class="col-piece">Becoming</th>
                  <th class="col-piece">Garland Waltz</th>
                  <th class="col-pref">Front Row?</th>
                  <th class="col-feeling">Feeling</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="dancer in sortedDancers" :key="dancer.id">
                  <td class="col-name">{{ dancer.firstName }} {{ dancer.lastName }}</td>
                  <td class="col-piece" :class="participationClass(dancer.waltzOfHours)">
                    {{ participationLabel(dancer.waltzOfHours) }}
                  </td>
                  <td class="col-piece">{{ dancer.waltzOfHours && dancer.waltzOfHours.participating ? (dancer.waltzOfHours.pirouetteChoice === 'knee' ? 'Knee' : dancer.waltzOfHours.pirouetteChoice === 'standing' ? 'Standing' : '') : '' }}</td>
                  <td class="col-piece">{{ dancer.waltzOfHours && dancer.waltzOfHours.participating ? (dancer.waltzOfHours.learnedOnDemand === 'yes' ? 'Yes' : dancer.waltzOfHours.learnedOnDemand === 'no' ? 'No' : '') : '' }}</td>
                  <td class="col-piece" :class="participationClass(dancer.aliceInWonderland)">
                    {{ participationLabel(dancer.aliceInWonderland) }}
                  </td>
                  <td class="col-piece" :class="participationClass(dancer.leJazzHot)">
                    {{ participationLabel(dancer.leJazzHot) }}
                  </td>
                  <td class="col-piece" :class="participationClass(dancer.becoming)">
                    {{ participationLabel(dancer.becoming) }}
                  </td>
                  <td class="col-piece" :class="participationClass(dancer.garlandWaltz)">
                    {{ participationLabel(dancer.garlandWaltz) }}
                  </td>
                  <td class="col-pref" :class="frontRowClass(dancer.checkIn)">
                    {{ frontRowLabel(dancer.checkIn) }}
                  </td>
                  <td class="col-feeling">{{ feelingLabel(dancer.checkIn) }}</td>
                </tr>
              </tbody>
            </table>

            <p v-if="sortedDancers.length === 0" class="mt-4">Loading dancers...</p>
          </v-container>
        </v-card-text>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<style scoped>
h2 {
  font-family: Quattrocento;
  font-weight: 400;
  font-size: 39px;
  letter-spacing: -.02em;
  line-height: 1em;
  margin-bottom: 16px;
}

.choreo-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Fanwood Text', Georgia, serif;
  font-size: 16px;
  margin-top: 16px;
}

.choreo-table th,
.choreo-table td {
  border: 1px solid #ddd;
  padding: 7px 10px;
  text-align: center;
  vertical-align: middle;
}

.choreo-table th {
  background: #f5f0eb;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #5a3e2b;
}

.col-name {
  text-align: left !important;
  min-width: 160px;
}

.col-piece {
  min-width: 90px;
}

.col-pref {
  min-width: 120px;
}

.col-feeling {
  text-align: left !important;
  min-width: 200px;
}

.yes {
  background: #e8f5e9;
  color: #2e7d32;
  font-weight: bold;
}

.no {
  color: #bbb;
}

.front-yes {
  background: #e3f2fd;
  color: #1565c0;
}

.front-if-needed {
  background: #fff8e1;
  color: #f57f17;
}

.front-no {
  color: #bbb;
}
</style>

<script>
import iabfAdminMixin from '@/utils/iabf-admin-mixin'
import { hasCorpsVideoAdmin } from '@/config/iabf-event'

export default {
  mixins: [iabfAdminMixin],
  data () {
    return {
      dancers: []
    }
  },
  created () {
    // 2026 fixed-corps sheet only — not used for 2027+ repertoire
    if (!hasCorpsVideoAdmin(this.adminFestivalYear)) {
      this.$router.replace('/iabf/admin')
      return
    }
    this.$store.dispatch('loadIABFDancers').then(() => {
      this.dancers = this.$store.getters.loadedIABFDancers
    })
  },
  computed: {
    sortedDancers () {
      return this.dancers.slice().sort((a, b) => a.firstName.localeCompare(b.firstName))
    }
  },
  methods: {
    participationLabel (piece) {
      if (!piece) return ''
      return piece.participating ? 'Y' : 'N'
    },
    participationClass (piece) {
      if (!piece) return ''
      return piece.participating ? 'yes' : 'no'
    },
    frontRowLabel (checkIn) {
      if (!checkIn || !checkIn.frontRow) return ''
      return checkIn.frontRow
    },
    frontRowClass (checkIn) {
      if (!checkIn || !checkIn.frontRow) return ''
      if (checkIn.frontRow === 'Yes please') return 'front-yes'
      if (checkIn.frontRow === 'I will if needed') return 'front-if-needed'
      return 'front-no'
    },
    feelingLabel (checkIn) {
      if (!checkIn || !checkIn.feeling || checkIn.feeling.length === 0) return ''
      return checkIn.feeling.join(', ')
    },
    exportCsv () {
      const headers = ['Name', 'Waltz of the Hours', 'WoH: Pirouette', 'WoH: Learned Broche?', 'Alice in Wonderland', 'Le Jazz Hot', 'Becoming', 'Garland Waltz', 'Front Row?', 'Feeling']
      const rows = this.sortedDancers.map(dancer => [
        `${dancer.firstName} ${dancer.lastName}`,
        this.participationLabel(dancer.waltzOfHours),
        dancer.waltzOfHours && dancer.waltzOfHours.participating ? (dancer.waltzOfHours.pirouetteChoice === 'knee' ? 'Knee' : dancer.waltzOfHours.pirouetteChoice === 'standing' ? 'Standing' : '') : '',
        dancer.waltzOfHours && dancer.waltzOfHours.participating ? (dancer.waltzOfHours.learnedOnDemand === 'yes' ? 'Yes' : dancer.waltzOfHours.learnedOnDemand === 'no' ? 'No' : '') : '',
        this.participationLabel(dancer.aliceInWonderland),
        this.participationLabel(dancer.leJazzHot),
        this.participationLabel(dancer.becoming),
        this.participationLabel(dancer.garlandWaltz),
        this.frontRowLabel(dancer.checkIn),
        this.feelingLabel(dancer.checkIn)
      ])
      const csv = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `iabf-${this.adminFestivalYear}-choreographer-sheet.csv`
      a.click()
      URL.revokeObjectURL(url)
    }
  }
}
</script>
